import type { BackupPreferences } from "@app/core";
import { useSettingsStore } from "../stores/settings.store.js";
import { toast } from "../stores/toast.store.js";
import { saveTextFile, openTextFile } from "./file-io.js";
import { gatherPreferences } from "./preferences-record.js";
import { tr } from "./tr.js";

/**
 * Standalone export/import of just the (non-secret) preferences — distinct from
 * a full data backup. Lets a user move their configuration between devices
 * without touching tasks/calendars. The API key is NEVER included (it isn't in
 * `gatherPreferences`). Also accepts a full backup file by reading its
 * `preferences` block.
 */
const KIND = { name: "Settings", extensions: ["json"], mime: "application/json" };

interface SettingsFile {
  kind: "reminders-settings";
  version: 1;
  exportedAt: string;
  preferences: BackupPreferences;
}

export async function exportSettingsFile(): Promise<void> {
  const file: SettingsFile = {
    kind: "reminders-settings",
    version: 1,
    exportedAt: new Date().toISOString(),
    preferences: gatherPreferences(),
  };
  const today = new Date().toISOString().slice(0, 10);
  await saveTextFile(JSON.stringify(file, null, 2), `reminders-settings-${today}.json`, KIND);
}

export async function importSettingsFile(): Promise<void> {
  const text = await openTextFile(KIND);
  if (text === null) return; // cancelled
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    toast.error(tr("toast.file.invalidJson"));
    return;
  }
  const prefs = (parsed as { preferences?: unknown })?.preferences ?? parsed;
  if (!prefs || typeof prefs !== "object") {
    toast.error(tr("toast.settings.noneInFile"));
    return;
  }
  await useSettingsStore.getState().applyBackupPreferences(prefs as BackupPreferences);
  toast.success(tr("toast.settings.imported"));
}
